import React, { useState } from 'react';
import { Search, UserX, Mail, Building2, Clock, AlertCircle, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import { useToast } from '../../../../hooks/use-toast';
import { searchProspect, unsubscribeProspect } from '../../../../services/AdminApis';

const FREE_DOMAINS = new Set([
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
  'icloud.com', 'aol.com', 'protonmail.com', 'yahoo.in', 'rediffmail.com',
  'msn.com', 'ymail.com', 'googlemail.com',
]);

const STATUS_BADGE = {
  PROSPECT: 'bg-blue-100 text-blue-800',
  SELLER: 'bg-green-100 text-green-800',
  UNSUBSCRIBED: 'bg-gray-100 text-gray-600',
  COLD_STORAGE: 'bg-yellow-100 text-yellow-800',
  DROPPED: 'bg-red-100 text-red-800',
};

const COMMS_STATUS_BADGE = {
  sent: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  pending: 'bg-yellow-100 text-yellow-700',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function SearchProspect() {
  const { toast } = useToast();
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('email');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [domainError, setDomainError] = useState('');
  const [confirmProspect, setConfirmProspect] = useState(null);
  const [unsubscribingId, setUnsubscribingId] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    setDomainError('');
    setResults(null);

    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return;

    if (searchType === 'domain' && FREE_DOMAINS.has(trimmed)) {
      setDomainError(`"${trimmed}" is a free/public email domain. Please enter a company domain.`);
      return;
    }

    if (searchType === 'email' && !trimmed.includes('@')) {
      setDomainError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const data = await searchProspect(trimmed, searchType);
      setResults(data);
      if (data.total === 0) {
        toast({ title: 'No results', description: 'No prospects found for this query.' });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to search prospects. Please try again.';
      toast({ variant: 'destructive', title: 'Search failed', description: detail });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUnsubscribe = (prospect) => {
    setConfirmProspect(prospect);
  };

  const handleUnsubscribe = async () => {
    if (!confirmProspect) return;
    const prospectId = confirmProspect.id;
    setConfirmProspect(null);
    setUnsubscribingId(prospectId);
    try {
      const data = await unsubscribeProspect(prospectId);
      setResults((prev) => ({
        ...prev,
        results: prev.results.map((p) =>
          p.id === prospectId ? { ...p, prospect_status: data.new_status } : p
        ),
      }));
      toast({ title: 'Unsubscribed', description: data.message });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to unsubscribe prospect.';
      toast({ variant: 'destructive', title: 'Error', description: detail });
    } finally {
      setUnsubscribingId(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-800 mb-6">Search Prospect</h1>

      {/* Search Bar */}
      <Card className="mb-6">
        <CardContent className="pt-5">
          <form onSubmit={handleSearch} className="flex flex-col gap-3">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setSearchType('email'); setDomainError(''); setResults(null); }}
                className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                  searchType === 'email'
                    ? 'bg-purple-700 text-white border-purple-700'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                By Email
              </button>
              <button
                type="button"
                onClick={() => { setSearchType('domain'); setDomainError(''); setResults(null); }}
                className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                  searchType === 'domain'
                    ? 'bg-purple-700 text-white border-purple-700'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                By Domain
              </button>
            </div>
            <div className="flex gap-3">
              <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setDomainError(''); }}
                placeholder={searchType === 'email' ? 'e.g. john@acme.com' : 'e.g. acme.com'}
                className="flex-1"
              />
              <Button type="submit" disabled={loading || !query.trim()} className="bg-purple-700 hover:bg-purple-800 text-white">
                <Search size={16} className="mr-2" />
                {loading ? 'Searching…' : 'Search'}
              </Button>
            </div>
            {domainError && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle size={14} /> {domainError}
              </p>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      {results && results.total === 0 && (
        <p className="text-gray-500 text-center py-8">No prospects found for this query.</p>
      )}

      {results && results.results.map((prospect) => (
        <Card key={prospect.id} className="mb-5 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex flex-col gap-1">
                <CardTitle className="text-lg font-semibold text-gray-800">
                  {prospect.prospect_name}
                </CardTitle>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Mail size={14} />
                  <span>{prospect.email_id}</span>
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      STATUS_BADGE[prospect.prospect_status] || 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {prospect.prospect_status}
                  </span>
                </div>
                {prospect.company_name && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Building2 size={14} />
                    <span>{prospect.company_name}</span>
                  </div>
                )}
                <div className="flex items-center gap-4 text-xs text-gray-400 mt-1">
                  <span>Emails sent: <strong>{prospect.email_sent_count}</strong></span>
                  {prospect.last_contacted && (
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      Last contacted: {formatDate(prospect.last_contacted)}
                    </span>
                  )}
                </div>
                {prospect.prospect_status === 'SELLER' && prospect.seller_profile_id && (
                  <div className="flex items-center gap-1 text-xs mt-1">
                    <span className="text-gray-500">Seller ID:</span>
                    <a
                      href={`/admin/sellers/${prospect.seller_profile_id}/view`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-0.5 text-purple-700 font-medium hover:underline"
                    >
                      {prospect.seller_profile_id}
                      <ExternalLink size={11} className="ml-0.5" />
                    </a>
                  </div>
                )}
              </div>

              {prospect.prospect_status === 'UNSUBSCRIBED' || prospect.prospect_status === 'SELLER'  ? (
                <Badge variant="secondary" className="bg-gray-100 text-gray-500 text-xs">
                  {prospect.prospect_status === 'UNSUBSCRIBED' ? 'Already unsubscribed' : 'Seller'}
                </Badge>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={unsubscribingId === prospect.id}
                  onClick={() => handleConfirmUnsubscribe(prospect)}
                  className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                >
                  <UserX size={14} className="mr-1" />
                  {unsubscribingId === prospect.id ? 'Unsubscribing…' : 'Unsubscribe'}
                </Button>
              )}
            </div>
          </CardHeader>

          <CardContent>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Recent Comms (last 5)
            </p>
            {prospect.recent_comms.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No comms recorded yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-gray-100">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs">Template / Subject</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prospect.recent_comms.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                          {formatDate(log.comms_date)}
                        </TableCell>
                        <TableCell className="text-xs">{log.comms_type}</TableCell>
                        <TableCell className="text-xs text-gray-700 max-w-xs truncate" title={log.message || log.comms_template_id || '—'}>
                          {log.message || log.comms_template_id || '—'}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              COMMS_STATUS_BADGE[log.status] || 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {log.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Unsubscribe Confirmation Dialog */}
      <AlertDialog open={!!confirmProspect} onOpenChange={(open) => !open && setConfirmProspect(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsubscribe Prospect</AlertDialogTitle>
            <AlertDialogDescription>
              This will stop all future prospect emails to{' '}
              <strong>{confirmProspect?.email_id}</strong>. This action updates their status
              to UNSUBSCRIBED immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleUnsubscribe}
            >
              Confirm Unsubscribe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
